/**
 * Shipments API Functions
 * Main shipment CRUD operations and KPIs
 */

import type { PaginationMetadata } from '@lognai/plugin-sdk';
import { apiFetch, buildQueryString, downloadFile, type DownloadResult } from './client';
import type {
  Shipment,
  CreateShipmentDto,
  UpdateShipmentDto,
  UpdateShipmentStatusDto,
  ShipmentFilters,
  ShipmentKpis,
} from '../types/shipments';

const BASE = '/api/shipments';

export interface PaginatedShipmentList {
  items: Shipment[];
  pagination: PaginationMetadata | null;
}

/**
 * List all shipments with optional filters and pagination
 */
/**
 * The list/export query string, including the custom-field filter.
 *
 * `filter.cf` is peeled off and written here as REPEATED params
 * (`filter[cf][key]=a&filter[cf][key]=b`), which the server reads as "any of".
 * The shared builder would instead join an array into one comma-separated value —
 * and a value like "Smith, John" would then be indistinguishable from two.
 */
/**
 * Date-range filters are held as `yyyy-mm-dd` in the user's timezone; the server
 * compares timestamps, so "from" becomes that day's start and "to" its end — an
 * inclusive range, as the user picked it.
 */
export const toServerDateRange = (filter: Record<string, unknown>): Record<string, unknown> => {
  const out: Record<string, unknown> = { ...filter };
  for (const [key, value] of Object.entries(filter)) {
    if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) continue;
    const [y, m, d] = value.split('-').map(Number);
    if (key.endsWith('From')) out[key] = new Date(y, m - 1, d, 0, 0, 0, 0).toISOString();
    else if (key.endsWith('To')) out[key] = new Date(y, m - 1, d, 23, 59, 59, 999).toISOString();
  }
  return out;
};

export const buildShipmentQuery = (filters?: ShipmentFilters): string => {
  if (!filters) return '';
  const { cf, tag, ...restOfFilter } = toServerDateRange((filters.filter ?? {}) as Record<string, unknown>) as {
    cf?: Record<string, string | string[]>;
    tag?: string[];
  } & Record<string, unknown>;
  const base = buildQueryString({ ...filters, filter: restOfFilter } as Record<string, unknown>);
  const extra = new URLSearchParams();
  for (const [key, raw] of Object.entries(cf ?? {})) {
    for (const value of ([] as string[]).concat(raw)) {
      if (value !== '') extra.append(`filter[cf][${key}]`, value);
    }
  }
  // Access tags are top-level and repeated: `tag=businessUnit:A&tag=businessUnit:B`.
  for (const value of tag ?? []) {
    if (value !== '') extra.append('tag', value);
  }
  const suffix = extra.toString();
  if (!suffix) return base;
  return base ? `${base}&${suffix}` : `?${suffix}`;
};

export const listShipments = async (
  filters?: ShipmentFilters
): Promise<PaginatedShipmentList> => {
  return apiFetch<PaginatedShipmentList>(`${BASE}/${buildShipmentQuery(filters)}`);
};

/**
 * Download every shipment matching the list's filters/search as XLSX (with
 * tracking dates). Pagination is ignored server-side; the export is capped.
 */
export const exportShipmentsXlsx = async (
  filters?: Pick<ShipmentFilters, 'filter' | 'sortBy' | 'sortOrder'>
): Promise<DownloadResult> => {
  const query = buildShipmentQuery(filters as ShipmentFilters | undefined);
  return downloadFile(`${BASE}/export${query}`, `shipments-${new Date().toISOString().slice(0, 10)}.xlsx`);
};

/**
 * Get a single shipment by ID
 */
export const getShipment = async (id: string): Promise<Shipment> => {
  return apiFetch<Shipment>(`${BASE}/${id}`);
};

/**
 * Create a new shipment
 */
/** What a shipment from an awarded Freight RFQ lane would be, for the form to start from; nothing is created. */
export interface AwardShipmentDraft {
  shipment: CreateShipmentDto;
  rfqNumber: string;
  trf: { id: string; number: string } | null;
}

export const getAwardShipmentDraft = (rfqId: string, laneId: string, quoteOptionId: string): Promise<AwardShipmentDraft> =>
  apiFetch<AwardShipmentDraft>(
    `${BASE}/from-freight-rfq-award/draft?${new URLSearchParams({ rfqId, laneId, quoteOptionId }).toString()}`
  );

/** The shipment for an awarded lane, as completed on the form; the lane is linked to it. */
export const createShipmentFromAward = (
  award: { rfqId: string; laneId: string; quoteOptionId: string },
  shipment: CreateShipmentDto
): Promise<Shipment> =>
  apiFetch<Shipment>(`${BASE}/from-freight-rfq-award`, {
    method: 'POST',
    body: JSON.stringify({ ...award, shipment }),
  });

export const createShipment = async (data: CreateShipmentDto): Promise<Shipment> => {
  return apiFetch<Shipment>(`${BASE}/`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

/**
 * Update an existing shipment
 */
export const updateShipment = async (
  id: string,
  data: UpdateShipmentDto
): Promise<Shipment> => {
  return apiFetch<Shipment>(`${BASE}/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

/**
 * Update shipment status
 */
export const updateShipmentStatus = async (
  id: string,
  data: UpdateShipmentStatusDto
): Promise<Shipment> => {
  return apiFetch<Shipment>(`${BASE}/${id}/status`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
};

/**
 * Cancel a shipment
 */
export const cancelShipment = async (
  id: string,
  reason?: string
): Promise<Shipment> => {
  return apiFetch<Shipment>(`${BASE}/${id}/cancel`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
};

/**
 * Get shipment KPIs
 */
export const getShipmentKpis = async (): Promise<ShipmentKpis> => {
  return apiFetch<ShipmentKpis>(`${BASE}/kpis`);
};

/**
 * Delete a shipment
 */
export const deleteShipment = async (id: string): Promise<void> => {
  return apiFetch<void>(`${BASE}/${id}`, {
    method: 'DELETE',
  });
};
